/**
 * /frontend/src/features/analytics/pages/BottleneckAnalyticsPage.js
 */

import React, { useEffect, useMemo } from 'react';
import useBottleneckAnalytics from '../hooks/useBottleneckAnalytics';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import ErrorState from '../components/shared/ErrorState';
import EmptyState from '../components/shared/EmptyState';
import SectionLoadRanking from '../components/bottleneck/SectionLoadRanking';
import SectionComparisonChart from '../components/bottleneck/SectionComparisonChart';
import ZoneTrafficPieChart from '../components/bottleneck/ZoneTrafficPieChart';
import ImbalanceAlertCards from '../components/bottleneck/ImbalanceAlertCards';
import { toBottleneckSectionSeries, toZoneTrafficPieSeries } from '../utils/chartDataTransformers';
import './BottleneckAnalyticsPage.css';

/**
 * Bottleneck analytics page.
 * @param {{request:Function,storeToken:string,storeUuid:string,startDate:string,endDate:string,refreshSignal?:unknown,onSyncingChange?:Function}} props
 * @returns {JSX.Element}
 */
const BottleneckAnalyticsPage = ({ request, storeToken, storeUuid, startDate, endDate, refreshSignal, onSyncingChange }) => {
  const hook = useBottleneckAnalytics({ request, storeToken, storeUuid, startDate, endDate, refreshSignal });
  const sections = useMemo(() => toBottleneckSectionSeries(hook.data?.sections), [hook.data?.sections]);
  const zones = useMemo(() => toZoneTrafficPieSeries(hook.data?.zones), [hook.data?.zones]);
  const isEmpty = useMemo(() => sections.length === 0 && zones.length === 0, [sections.length, zones.length]);
  const syncing = hook.isLoading || hook.isRefreshing;

  useEffect(() => {
    if (typeof onSyncingChange !== 'function') return () => null;
    onSyncingChange(syncing);
    return () => onSyncingChange(false);
  }, [onSyncingChange, syncing]);

  if (hook.isLoading) return <LoadingSkeleton />;
  if (hook.isError) return <ErrorState onRetry={hook.refetch} />;
  if (isEmpty) return <EmptyState message="No bottleneck analytics data for the selected period." />;

  return (
    <div className="analytics-page-stack">
      <ImbalanceAlertCards data={sections} />
      <div className="analytics-page-grid-2">
        <SectionLoadRanking data={sections} />
        <ZoneTrafficPieChart data={zones} />
      </div>
      <SectionComparisonChart data={sections} />
    </div>
  );
};

export default BottleneckAnalyticsPage;
